import { Component, computed, OnInit, signal, Signal, ViewChild } from '@angular/core';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Catch } from '../shared/catch.model';
import { LocalStorageService } from '../shared/local-storage.service';
import { FishingEvent } from '../shared/fishing-event.model';
import { TrawlSet } from '../shared/trawl-set.model';


/**
 * A formatted table detailing all catch on a set or on all sets attached to a delivery.
 */
@Component({
    selector: 'app-summary-report',
    templateUrl: './summary-report.component.html',
    styleUrls: ['./summary-report.component.css'],
    standalone: false
})
export class SummaryReportComponent implements OnInit {

  @ViewChild(MatTable) table!: MatTable<Catch>;
  computedSets: Signal<(FishingEvent | TrawlSet)[]> = signal([]);
  computedCatch: Signal<Catch[]> = signal([]);
  catchTableColumns: string[] = ['Species', 'RetainedCount', 'RetainedWeight', 'ReleasedCount', 'ReleasedWeight'];
  title: string = '';
  fishery: string = '';



  constructor(public dataSync: LocalStorageService, private route: ActivatedRoute, public router: Router) { }

  /**
   * This page can be navigated to from: set list, current delivery, or a completed delivery.
   * We get all recorded catch and put it into the table.
   */ 
  ngOnInit(): void {
    this.fishery = <string>this.route.snapshot.paramMap.get('delivery');
    let index = Number.parseInt(<string>this.route.snapshot.paramMap.get('index'));
    let isDraft = this.route.snapshot.queryParams.draft == 'true';
    let from = this.route.snapshot.queryParams.from;

    switch (this.fishery) {
      case 'menu':
        this.title = 'All Sets';
        let fishery = from == 0 ? 'Groundfish' : from == 1 ? 'HMS' : 'Trawl';
        this.computedSets = computed(() => from == 2 ? this.dataSync.user_trawlTrip().sets : this.dataSync.user_trip().sets.filter(s => s.fishery == fishery));
        break;
      case 'Trawl':
        this.title = index == -1 ? 'Current Trawl Delivery' : "Logbook " + this.dataSync.received_trawlLogbooks()[index].logbookId.substring(17);
        let tDelivery = this.dataSync.received_trawlLogbooks()[index];
        let tLogbook = this.dataSync.edited_trawlLogbook();
        this.computedSets = computed(() => index == -1 ? this.dataSync.user_trawlTrip().trawlDelivery.sets : (tDelivery.logbookId == tLogbook.logbookId) ? tLogbook.sets : tDelivery.sets);
        break;
      default:
        this.title = index == -1 ? `Current ${this.fishery} Delivery` : isDraft ? 'Draft Trip' : "Logbook " + this.dataSync.received_logbooks()[index].logbookId.substring(17);
        let delivery = isDraft ? this.dataSync.hms_drafts()[index] : this.dataSync.received_logbooks()[index];
        let logbook = this.dataSync.edited_groundfish().logbookId ? this.dataSync.edited_groundfish() : this.dataSync.edited_hms();
        this.computedSets = computed(() => index == -1 ? (this.fishery == 'Groundfish' ? this.dataSync.user_trip().groundfishDelivery.sets : 
        this.dataSync.user_trip().hmsDelivery.sets) : isDraft ? this.dataSync.hms_drafts()[index].sets : (delivery.logbookId == logbook.logbookId) ? logbook.sets : delivery.sets);
        break;
    }

    //Need to call render rows after this?
    this.computedCatch = computed(() => {
      let species = new Map<string, Catch>();
      this.computedSets().forEach(set => {
        set.catchItems.forEach(cth => {
          //These need to be numbers but are being interpreted as strings?
          if (species.has(cth.species.name)) {
            let c = species.get(cth.species.name);
            if (c?.releasedCount !== undefined) c!.releasedCount +=  cth.releasedCount ?? 0;
            if (c?.retainedCount !== undefined) c!.retainedCount += cth.retainedCount ?? 0;
            if (c?.releasedWeight !== undefined) c!.releasedWeight += cth.releasedWeight ?? 0;
            if (c?.retainedWeight !== undefined) c!.retainedWeight += cth.retainedWeight ?? 0;
            species.set(c!.species.name, c!);
          } else {
            species.set(cth.species.name, Catch.transformAnonymous([cth])[0]);
          }
        });
      });
      return [...species.values()];
    });
  }


  navigateBack() {
    let index = Number.parseInt(<string>this.route.snapshot.paramMap.get('index'));
    let isDraft = this.route.snapshot.queryParams.draft == 'true';

    const deliveryDetailRoute = this.fishery === 'menu' ? 'menu' : 
                                this.fishery === 'Trawl' ? 'trawl_delivery' : 
                                this.fishery === 'Groundfish' ? 'delivery_detail' : 'hms_delivery_detail';
    const logbook = this.fishery === 'Trawl' ? this.dataSync.edited_trawlLogbook() : 
                    this.fishery === 'Groundfish' ? this.dataSync.edited_groundfish() : this.dataSync.edited_hms();

    if (index != -1) {
      let delivery = this.fishery === 'Trawl' ? this.dataSync.received_trawlLogbooks()[index] : this.dataSync.received_logbooks()[index];
      this.router.navigate([deliveryDetailRoute, isDraft ? 3 : delivery.logbookId == logbook.logbookId ? 2 : 1, index], { queryParams: { previous: 'deliveries_list' } });
    } else {
      this.router.navigate([deliveryDetailRoute, 0, -1], { queryParams: { previous: 'menu' } });
    }
  }

}
